import { useState } from 'react';
import { useGetAllCategoriesPaginatedQuery, useGetCategoriesQuery } from '@/services/api/category/categoryApi';

/**
 * Custom hook for managing category data
 * Provides a clean interface for category-related operations
 */
export const useCategoryData = () => {
  // ==========================================
  // Pagination State
  // ==========================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State
  // ==========================================
  const [search, setSearch] = useState("");

  // ==========================================
  // Pagination Handlers
  // ==========================================
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (per_page) => {
    setPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handler
  // ==========================================
  const handleSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setSearch(trimmedSearch);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const {
    data: categoriesResponse = {},
    isLoading,
    isFetching,
    error,
    refetch
  } = useGetAllCategoriesPaginatedQuery({
    page: pagination.page,
    per_page: pagination.per_page,
    search: search,
  });

  // console.log(categoriesResponse, "Fetched Categories Data");

  // Extract data and pagination info - handle both direct and nested response
  const responseData = categoriesResponse.original || categoriesResponse;
  const categoriesData = responseData.data || [];
  const paginationInfo = {
    current_page: responseData.current_page || 1,
    last_page: responseData.last_page || 1,
    per_page: responseData.per_page || 10,
    total: responseData.total || 0,
  };

  // Calculate statistics
  const totalCategories = paginationInfo.total || categoriesData.length;
  // Since the API doesn't provide status, we'll assume all categories are active
  const activeCategories = categoriesData.length;
  const inactiveCategories = 0;

  return {
    // Data
    categoriesData,
    totalCategories,
    activeCategories,
    inactiveCategories,
    
    // Pagination info
    paginationInfo,
    
    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,
    
    // Search state and handler
    search,
    handleSearch,
    
    // Loading states
    isLoading,
    isFetching,
    
    // Error states
    error,
    isError: !!error,
    
    // Actions
    refetch
  };
};
